/**
 * Frontend observability helpers — browser-side analogue of
 * backend/handlers/common.py::timed() / log_event() (rev-026).
 *
 * `timed(operation, fn, dims)` records duration + success/error for every
 * invocation and re-throws the original Error so callers stay simple.
 * `logEvent(event, fields)` emits an ad-hoc structured event.
 *
 * Structured-logging contract:
 *   - Every emission is a SINGLE JSON object per line. Fields: `event`,
 *     `level`, `ts`, plus caller-supplied dimensions. This matches the
 *     backend's log_event() shape so CloudWatch Logs Insights / RUM can
 *     parse both with one schema (rev-026, cross-pattern).
 *   - Emission is routed through a single sink function so the
 *     "use structured logging" rule finds exactly one write point to audit.
 *   - `window.__skywrenchLogSink` is an optional override so a future
 *     CloudWatch RUM or fetch-based transport can replace the default
 *     without touching call sites.
 */

/** Additional dimensions to attach to a telemetry event. Values are scalar. */
type Dimensions = Record<string, string | number | boolean>;

type LogLevel = 'info' | 'error';

interface StructuredRecord {
  event: string;
  level: LogLevel;
  ts: string;
  [key: string]: unknown;
}

type LogSink = (record: StructuredRecord) => void;

declare global {
  interface Window {
    __skywrenchLogSink?: LogSink;
  }
}

/** Return the configured sink or a default that writes one JSON line. */
function getSink(): LogSink {
  if (typeof window !== 'undefined' && window.__skywrenchLogSink) {
    return window.__skywrenchLogSink;
  }
  return defaultSink;
}

/**
 * Default sink: one JSON object per line. Kept as the ONLY place this module
 * touches the browser console so the structured-logging contract has a
 * single, auditable write point.
 */
function defaultSink(record: StructuredRecord): void {
  const line = JSON.stringify(record);
  /* eslint-disable no-console */
  if (record.level === 'error') {
    console.error(line);
  } else {
    console.info(line);
  }
  /* eslint-enable no-console */
}

/** Return the highest-resolution clock available. */
function now(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

/** Emit a structured telemetry event. */
export function logEvent(event: string, fields?: Dimensions): void {
  const level: LogLevel = event.endsWith('.error') ? 'error' : 'info';
  getSink()({ event, level, ts: new Date().toISOString(), ...fields });
}

/**
 * Wrap an async operation with duration + outcome instrumentation.
 *
 * Emits `<operation>.success` on resolve and `<operation>.error` on throw,
 * each with `durationMs` and any caller-supplied dimensions. The original
 * Error is re-thrown unchanged — `timed()` observes, never transforms.
 */
export async function timed<T>(
  operation: string,
  fn: () => Promise<T>,
  dims?: Dimensions,
): Promise<T> {
  const start = now();
  try {
    const result = await fn();
    logEvent(`${operation}.success`, { durationMs: Math.round(now() - start), ...dims });
    return result;
  } catch (err: unknown) {
    logEvent(`${operation}.error`, {
      durationMs: Math.round(now() - start),
      errorType: err instanceof Error ? err.name || err.constructor.name : 'Unknown',
      errorMessage: err instanceof Error ? err.message : String(err),
      ...dims,
    });
    throw err;
  }
}
